import { Injectable } from "@angular/core";
import { APP_CONFIG } from "../../environment";
import { Chooser } from "@ionic-native/chooser";
import { Camera, CameraOptions } from "@ionic-native/camera";
import { Platform } from "ionic-angular/platform/platform";
import { File } from "@ionic-native/file";
import { FilePath } from "@ionic-native/file-path";
import { LoggerProvider, LoggerEvent } from '../../providers/logger/logger';
import { ErrorCodeType } from "../../model/types/error-code-type";
import { TranslateService } from "@ngx-translate/core";
import { RouteProvider } from "../../providers/route/route";

declare let navigator;
@Injectable()
export class UploadProvider {
  errorType: string;
  sourceType: string;
  validateFileAndUpdate;
  constructor(
    private camera: Camera,
    private platform: Platform,
    private fileChooser: Chooser,
    private file: File,
    private filePath: FilePath,
    private contentProvider: TranslateService,
    private routeProvider: RouteProvider
  ) {}

  async captureImage(useAlbum: boolean, sourceType?: string, validateFileAndUpdate?: any) {
    this.errorType = "";
    this.sourceType = sourceType;
    this.validateFileAndUpdate = validateFileAndUpdate;
    const options: CameraOptions = {
      sourceType: useAlbum
        ? this.camera.PictureSourceType.PHOTOLIBRARY
        : this.camera.PictureSourceType.CAMERA,
      destinationType: this.camera.DestinationType.FILE_URI,
      quality: 70,
      targetWidth: 800,
      targetHeight: 800,
      encodingType: this.camera.EncodingType.JPEG,
      correctOrientation: true,
    };
    try {
      LoggerProvider.info(this, "Doc Upload open camera/ album to upload image");
      if (this.platform.is('cordova') && navigator.camera) {
        navigator.camera.getPicture(
          (imageData) => this.cameraSuccess(imageData),
          (message) => this.cameraError(message),
          options
        );
      } else {
        // Fallback for browser for dev
        this.fallbackToBrowserFilePicker();
      }
    } catch (e) {
      LoggerProvider.exception(this, e, LoggerEvent.DocUpload, "Error while opening camera/ album");
      this.showGenericErrorAlert();
    }
  }

  fallbackToBrowserFilePicker() {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    (input as any).capture = this.sourceType === 'camera' ? 'environment' : '';

    input.onchange = () => {
      const file = input.files[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = () => {
        const base64Image = reader.result as string;
        this.cameraSuccess(base64Image, file.name, file.type);
      };
      reader.onerror = () => {
        this.cameraError("Failed to read image");
      };
      reader.readAsDataURL(file);
    };

    input.click();
  }

  async cameraSuccess(imageData, fileNameFromBrowser?: string, mimeTypeFromBrowser?: string) {
    LoggerProvider.info(this, "Got data from doc upload plugin", imageData);
    let fileName = fileNameFromBrowser || '';
    let base64;
    let fileSizeInKB;
    const fileURI = imageData;
    try {
      if (fileURI) {
        if (this.platform.is('cordova')) {
          if (this.platform.is("ios")) {
            fileName = this.getFileNameFromFileURI(fileURI);
            base64 = await this.convertFileURIToBase64(fileURI, fileName);
          } else {
            let androidFileObj = await this.getAndroidBase64(fileURI);
            if (!androidFileObj.base64)
              androidFileObj = await this.getAndroidBase64(fileURI, true);
            base64 = androidFileObj.base64;
            fileName = androidFileObj.androidFileName;
          }
        } else {
          base64 = fileURI;
          if (!fileName && mimeTypeFromBrowser) {
            const ext = mimeTypeFromBrowser.split('/')[1];
            fileName = `dummyDev.${ext}`;
          }
        }

        if (base64) {
          fileSizeInKB = this.calculateImageSize(base64);
        } else {
          this.errorType = ErrorCodeType.FileGenericError;
        }
      }
    } catch (e) {
      this.errorType = ErrorCodeType.FileGenericError;
      if (e == ErrorCodeType.FileNotSelected) {
        this.errorType = "";
        LoggerProvider.info(this, "Doc Upload no file selected");
      }
      const extensionCheck = this.pluginFailExtensionCheck(fileURI);

      if (!extensionCheck) {
        this.errorType = ErrorCodeType.FileType;
      }

      LoggerProvider.exception(this, e, LoggerEvent.DocUpload, "Error while opening camera/ album");
    }

    if(this.validateFileAndUpdate)
      this.validateFileAndUpdate({ fileName, base64, fileURI, fileSizeInKB, sourceType: this.sourceType, fileError: this.errorType });
  }

  async cameraError(message) {
    LoggerProvider.exception(this, message, LoggerEvent.DocUpload, "Error while opening camera/ album");

    this.errorType = ErrorCodeType.FileGenericError;
    if(this.validateFileAndUpdate)
      this.validateFileAndUpdate({ fileName:"", base64: "", fileURI: "", fileSizeInKB: "", sourceType: this.sourceType, fileError: this.errorType });
  }

  async getAndroidBase64(fileURI: string = "", useResolveNative: boolean = false) {
    const { androidFileName, androidFilePath } =
    await this.getAndroidFileName(fileURI, useResolveNative);
    const base64 = await this.convertFileURIToBase64(
      androidFilePath, 
      androidFileName
    );
    return { base64, androidFileName };
  }

  pluginFailExtensionCheck(filePath) {
    
    let isExtensionCorrect: boolean = true;
    if (filePath) {
      const fileName = filePath.substring(filePath.lastIndexOf("/") + 1);
      const validFileType = this.validateFileType(fileName);
      if (!validFileType) {
        isExtensionCorrect = false;
      }
    }

    return isExtensionCorrect;
  }

  validateFileSizeAndType(
    base64: string,
    filePath: string,
    fileName: string,
    sourceType: string
  ) {
    // Fallback only if NOT on a real device and base64 input is detected
    const isBrowser = !this.platform.is('cordova');
    const isBase64 = base64 && base64.startsWith('data:image');

    if (!base64) {
      return ErrorCodeType.FileNotValid;
    }

    if (isBrowser && isBase64) {
      const mimeMatch = base64.match(/^data:(image\/[a-zA-Z]+);base64,/);
      if (mimeMatch && mimeMatch[1]) {
        const ext = mimeMatch[1].split('/')[1];
        fileName = fileName || `dummyDev.${ext}`;
      } else {
        fileName = fileName || `dummyDev.jpg`;
      }

      // Clean base64
      base64 = this.sanitizeBase64(base64);
    }

    const fileTypeExtension = this.validateFileType(fileName);
    if (!fileTypeExtension) {
      return ErrorCodeType.FileType;
    }

    const mimeMap = {
      jpg: 'image/jpeg',
      jpeg: 'image/jpeg',
      png: 'image/png',
      pdf: 'application/pdf',
    };

    const supportedFileType = mimeMap[fileTypeExtension];

    // Checking file size
    const sizeInKB = this.calculateImageSize(base64);
    if (sizeInKB > 5000) {
      return ErrorCodeType.FileSize;
    }

    return {
      supportedFileType,
      fileName,
    };
  }

  async getAndroidFileName(fileURI: string, useResolveNative: boolean = false) {
    try {
      LoggerProvider.info(this, "Doc Upload For Android get android file name and path");
      let filePath = fileURI;
      if(useResolveNative)
        filePath = await this.filePath.resolveNativePath(fileURI);
      
      if(filePath.indexOf("?") > 1) {
        filePath = filePath.substring(0, filePath.indexOf("?"))
      }

      return {
        androidFileName: filePath.substring(filePath.lastIndexOf("/") + 1),
        androidFilePath: filePath,
      };
    } catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.DocUpload, "Error while fetching android file path and name"); 
    }
  }

  validateFileType(fileNameParam: string) {
    const supportedFiles = ["jpeg", "jpg", "png", "pdf"];
    let fileName;
    let extension;
    let pathSplit;
    let res = null;

    fileName = fileNameParam;
    pathSplit = fileName.split(".");
    extension = pathSplit[pathSplit.length - 1].toLocaleLowerCase();

    if (supportedFiles.indexOf(extension) !== -1) {
      res = extension;
    }
    return res;
  }

  calculateImageSize(base64String) {
    let padding;
    let inBytes;
    let base64StringLength;
    if (base64String && base64String.endsWith("==")) {
      padding = 2;
    } else if (base64String && base64String.endsWith("=")) {
      padding = 1;
    } else {
      padding = 0;
    }

    base64StringLength = base64String.length;
    
    inBytes = (base64StringLength / 4) * 3 - padding;
    
    const kBytes = inBytes / 1000;
    return parseFloat(kBytes.toString());
  }

  formatBytes(bytes, decimals = 2) {
    if (bytes === 0) return "0 Bytes";

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
  }

  async chooseFile() {
    let fileURI;
    let fileName;
    let base64;
    let fileSizeInKB;
    let mediaType;
    let fileObj;
    try {
      this.errorType = "";
      LoggerProvider.info(this, "Doc Upload choose files from library");
      //image/*, text/*, application/pdf, application/vnd.google-apps.photo
      if (this.platform.is("ios")) 
        fileObj = await this.fileChooser.getFile("image/*, text/*, application/pdf");
       else 
        fileObj = await this.fileChooser.getFile("*/*");
      
      if (fileObj) {
        fileName = fileObj.name;
        base64 = fileObj.dataURI;
        fileURI = fileObj.uri;
        mediaType = fileObj.mediaType;
        const pathSplit = fileName.split(".");
        
        if(pathSplit.length == 1) { // Check if file name don't have it's extension Problem with Google Drive
          fileName = `${fileName}.${mediaType}`;
        }

        if (base64 !== null) {
          fileSizeInKB = this.calculateImageSize(base64);
          base64 = this.validateBase64(base64);
        } else {
          this.errorType = ErrorCodeType.FileGenericError;
        }
      }
      
    } catch (e) {
      this.errorType = ErrorCodeType.FileGenericError;
      if(e && e == ErrorCodeType.FileType) {
        this.errorType = ErrorCodeType.FileType;
      }
      LoggerProvider.exception(this, e, LoggerEvent.DocUpload, "Error while choosing file for upload");
    }

    return { fileName, base64, fileURI, fileSizeInKB, fileError: this.errorType };
  }

  getFileNameFromFileURI(fileURI: string) {
    const fileName = fileURI.substring(fileURI.lastIndexOf("/") + 1);
    return fileName;
  }

  async convertFileURIToBase64(fileURI: string, fileName: string): Promise<string> {
    try {
      LoggerProvider.info(this, "Doc Upload convert image fileURI to base64");

      if (this.platform.is('cordova')) {
        if (fileURI) {
          const filePath = fileURI.substring(0, fileURI.lastIndexOf("/") + 1);
          let base64 = await this.file.readAsDataURL(filePath, fileName);
          if (base64) {
            base64 = this.validateBase64(base64);
          }
          return base64;
        }
      } else {
        // Browser fallback for developmentt
        return await this.convertBrowserFileToBase64(fileURI);
      }
    } catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.DocUpload, "Error while converting fileURI to base64");
    }
  }

  async convertBrowserFileToBase64(dataURL: string): Promise<string> {
    try {
      if (dataURL.startsWith('data:image')) {
        return dataURL;
      }
      LoggerProvider.info(this, "Browser fallback did not receive a base64 string");
      return null;
    } catch (err) {
      LoggerProvider.exception(this, err, LoggerEvent.DocUpload, "Browser base64 conversion failed");
      return null;
    }
  }


  validateBase64(base64: string) {
    if (base64.includes("base64")) {
      base64 = base64.substring(base64.indexOf("base64") + 7);
    }
    return base64;
  }

  showGenericErrorAlert() {
    const params = { title: this.contentProvider.instant('page.transferDetailsPage.uploadDocs.errorGenericHeader'),
      subtitle: this.contentProvider.instant('page.transferDetailsPage.uploadDocs.errorGenericBody'),
      primaryButtonText: this.contentProvider.instant('global.gotItButton')
    };
    LoggerProvider.info(this, "Doc Upload Error while uploading the file");
    this.routeProvider.openActionSheet(this.routeProvider.pages.ModalAlertPage, params, null);
  }

  sanitizeBase64(base64: string): string {
    return base64.includes(',') ? base64.split(',')[1] : base64;
}

}



// WEBPACK FOOTER //
// ./src/providers/upload/upload.ts